/**
 * ClusterDetail — right-panel detail view for a selected wish cluster.
 * Shows editable fields, representative wishes, and action buttons.
 */

import { useState, useEffect } from 'react';
import { Save, Bell, GitMerge, Trash2, X } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/common/Modal';
import {
  useClusterDetail,
  useUpdateCluster,
  useMergeClusters,
  useNotifyCluster,
  useDeleteWish,
} from '../hooks';
import type { ClusterStatus, AdminClusterItem, ClusterUpdateRequest } from '../types';
import { STATUS_LABELS, STATUS_STYLES } from './ClusterCard';
import clsx from 'clsx';

interface ClusterDetailProps {
  clusterId: number;
  allClusters: AdminClusterItem[];
  onClose: () => void;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

export function ClusterDetail({ clusterId, allClusters, onClose }: ClusterDetailProps) {
  const { data: cluster, isLoading } = useClusterDetail(clusterId);
  const updateMutation = useUpdateCluster();
  const mergeMutation = useMergeClusters();
  const notifyMutation = useNotifyCluster();
  const deleteWishMutation = useDeleteWish();

  const [form, setForm] = useState<ClusterUpdateRequest>({});
  const [showNotifyConfirm, setShowNotifyConfirm] = useState(false);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState<string>('');

  useEffect(() => {
    if (cluster) {
      setForm({
        title: cluster.title,
        summary: cluster.summary ?? '',
        status: cluster.status,
        status_note: cluster.status_note ?? '',
        representative_excerpt: cluster.representative_excerpt ?? '',
      });
    }
  }, [cluster]);

  const handleSave = async () => {
    if (!cluster) return;
    const { ...updateData } = form;
    await updateMutation.mutateAsync({ id: clusterId, update: updateData });
  };

  const handleNotify = async () => {
    await notifyMutation.mutateAsync(clusterId);
    setShowNotifyConfirm(false);
  };

  const handleMerge = async () => {
    if (!mergeTargetId) return;
    await mergeMutation.mutateAsync({
      from_id: clusterId,
      into_id: Number(mergeTargetId),
    });
    setShowMergeDialog(false);
    onClose();
  };

  const otherOpenClusters = allClusters.filter(
    (c) => c.id !== clusterId && c.status === 'open'
  );

  if (isLoading) {
    return (
      <div className="p-6 text-center text-gray-500 text-sm">加载中...</div>
    );
  }

  if (!cluster) {
    return (
      <div className="p-6 text-center text-gray-500 text-sm">未找到</div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-white flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={clsx(
              'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0',
              STATUS_STYLES[cluster.status]
            )}
          >
            {STATUS_LABELS[cluster.status]}
          </span>
          <span className="text-sm font-semibold text-gray-900 truncate">{cluster.title}</span>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0 ml-2">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="bg-gray-50 rounded-lg p-2">
            <p className="text-lg font-bold text-gray-900">{cluster.wish_count}</p>
            <p className="text-xs text-gray-500">愿望数</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2">
            <p className="text-xs font-medium text-gray-700 uppercase">{cluster.language}</p>
            <p className="text-xs text-gray-500">语言</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2">
            <p className="text-xs font-medium text-gray-700">
              {cluster.notified_at ? formatDate(cluster.notified_at) : '未通知'}
            </p>
            <p className="text-xs text-gray-500">通知时间</p>
          </div>
        </div>

        {/* Edit form */}
        <Card className="space-y-3 p-4">
          <Input
            label="标题"
            value={form.title ?? ''}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
          />
          <Textarea
            label="摘要"
            value={form.summary ?? ''}
            onChange={(e) => setForm((p) => ({ ...p, summary: e.target.value }))}
            rows={3}
          />
          <Select
            label="状态"
            value={form.status ?? 'open'}
            onChange={(e) =>
              setForm((p) => ({ ...p, status: e.target.value as ClusterStatus }))
            }
            options={[
              { value: 'open', label: '开放' },
              { value: 'planned', label: '已规划' },
              { value: 'in_progress', label: '进行中' },
              { value: 'done', label: '已完成' },
              { value: 'rejected', label: '已拒绝' },
            ]}
          />
          <Textarea
            label="状态说明"
            value={form.status_note ?? ''}
            onChange={(e) => setForm((p) => ({ ...p, status_note: e.target.value }))}
            rows={2}
          />
          <Textarea
            label="代表性摘录"
            value={form.representative_excerpt ?? ''}
            onChange={(e) =>
              setForm((p) => ({ ...p, representative_excerpt: e.target.value }))
            }
            rows={2}
          />
          <div className="flex justify-end">
            <Button size="sm" onClick={handleSave} loading={updateMutation.isPending}>
              <Save className="w-4 h-4 mr-1" />
              保存
            </Button>
          </div>
        </Card>

        {/* Representative wishes */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            代表愿望 ({cluster.representative_wishes.length})
          </h3>
          {cluster.representative_wishes.length === 0 ? (
            <p className="text-xs text-gray-400">暂无代表愿望</p>
          ) : (
            <ul className="space-y-2">
              {cluster.representative_wishes.map((w) => (
                <li
                  key={w.id}
                  className="bg-gray-50 rounded-lg px-3 py-2 flex items-start justify-between gap-2 group"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-gray-800">{w.content}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{formatDate(w.created_at)}</p>
                  </div>
                  <button
                    onClick={() => deleteWishMutation.mutate(w.id)}
                    disabled={deleteWishMutation.isPending}
                    className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5"
                    title="删除"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 pt-2 border-t border-gray-100">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowNotifyConfirm(true)}
            disabled={cluster.status === 'done'}
            className="w-full justify-center"
          >
            <Bell className="w-4 h-4 mr-1" />
            通知用户
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setMergeTargetId(''); setShowMergeDialog(true); }}
            disabled={otherOpenClusters.length === 0}
            className="w-full justify-center"
          >
            <GitMerge className="w-4 h-4 mr-1" />
            合并到...
          </Button>
        </div>
      </div>

      {/* Notify Confirm Modal */}
      <Modal
        isOpen={showNotifyConfirm}
        onClose={() => setShowNotifyConfirm(false)}
        title="确认通知用户"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            将向所有订阅此愿望群组的用户发送通知，并将状态标记为 <strong>已完成</strong>。确定继续？
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowNotifyConfirm(false)}>
              取消
            </Button>
            <Button size="sm" onClick={handleNotify} loading={notifyMutation.isPending}>
              确认通知
            </Button>
          </div>
        </div>
      </Modal>

      {/* Merge Dialog */}
      <Modal
        isOpen={showMergeDialog}
        onClose={() => setShowMergeDialog(false)}
        title="合并到另一个群组"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            当前群组 &ldquo;{cluster.title}&rdquo; 将被合并到目标群组，所有愿望转移，当前群组删除。
          </p>
          <Select
            label="目标群组"
            value={mergeTargetId}
            onChange={(e) => setMergeTargetId(e.target.value)}
            options={[
              { value: '', label: '请选择...' },
              ...otherOpenClusters.map((c) => ({
                value: String(c.id),
                label: `${c.title} (${c.wish_count} 个愿望)`,
              })),
            ]}
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowMergeDialog(false)}>
              取消
            </Button>
            <Button
              size="sm"
              onClick={handleMerge}
              loading={mergeMutation.isPending}
              disabled={!mergeTargetId}
            >
              确认合并
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
